import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';
import {
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    ArrowsUpDownIcon,
    BookOpenIcon,
    GlobeAltIcon,
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    LinkIcon,
} from '@heroicons/react/24/outline';
import { addressAPI } from '../../api/api';

const AddressBookPage = () => {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [globalFilter, setGlobalFilter] = useState('');
    const [debouncedFilter, setDebouncedFilter] = useState('');
    const [sorting, setSorting] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const PAGE_SIZE = 50;

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilter(globalFilter);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [globalFilter]);

    const fetchAddresses = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await addressAPI.getAddresses({
                page: currentPage,
                page_size: PAGE_SIZE,
                search: debouncedFilter,
            });
            if (response.success) {
                setAddresses(response.addresses || []);
                setTotalRecords(response.total || 0);
                setTotalPages(response.total_pages || 1);
            } else {
                setError('Veriler yüklenemedi.');
            }
        } catch {
            setError('Sunucu bağlantı hatası.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedFilter]);

    useEffect(() => {
        fetchAddresses();
    }, [fetchAddresses]);

    const columns = useMemo(() => [
        {
            accessorKey: 'name',
            header: 'AD / FİRMA',
            cell: ({ getValue }) => (
                <span className="font-semibold text-slate-900 dark:text-white">
                    {getValue() || '—'}
                </span>
            ),
        },
        {
            accessorKey: 'lname',
            header: 'SOYAD',
            cell: ({ getValue }) => (
                <span className="text-slate-700 dark:text-slate-300">{getValue() || '—'}</span>
            ),
        },
        {
            accessorKey: 'country',
            header: 'ÜLKE',
            cell: ({ getValue }) =>
                getValue() ? (
                    <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                        <GlobeAltIcon className="w-3.5 h-3.5 shrink-0" />
                        {getValue()}
                    </span>
                ) : (
                    <span className="text-slate-400">—</span>
                ),
        },
        {
            accessorKey: 'city',
            header: 'ŞEHİR',
            cell: ({ getValue }) =>
                getValue() ? (
                    <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                        <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
                        {getValue()}
                    </span>
                ) : (
                    <span className="text-slate-400">—</span>
                ),
        },
        {
            accessorKey: 'phone',
            header: 'TELEFON',
            cell: ({ getValue }) =>
                getValue() ? (
                    <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                        <PhoneIcon className="w-3.5 h-3.5 shrink-0" />
                        {getValue()}
                    </span>
                ) : (
                    <span className="text-slate-400">—</span>
                ),
        },
        {
            accessorKey: 'email',
            header: 'E-POSTA',
            cell: ({ getValue }) =>
                getValue() ? (
                    <a
                        href={`mailto:${getValue()}`}
                        className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline"
                    >
                        <EnvelopeIcon className="w-3.5 h-3.5 shrink-0" />
                        {getValue()}
                    </a>
                ) : (
                    <span className="text-slate-400">—</span>
                ),
        },
        {
            accessorKey: 'address',
            header: 'ADRES',
            cell: ({ getValue }) => (
                <span className="text-slate-600 dark:text-slate-400 max-w-[200px] truncate block" title={getValue()}>
                    {getValue() || '—'}
                </span>
            ),
        },
        {
            accessorKey: 'website',
            header: 'WEBSİTE',
            cell: ({ getValue }) =>
                getValue() ? (
                    <a
                        href={getValue()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline"
                    >
                        <LinkIcon className="w-3.5 h-3.5 shrink-0" />
                        {getValue()}
                    </a>
                ) : (
                    <span className="text-slate-400">—</span>
                ),
        },
        {
            accessorKey: 'created_at',
            header: 'KAYIT TARİHİ',
            cell: ({ getValue }) => {
                const val = getValue();
                if (!val) return <span className="text-slate-400">—</span>;
                const date = new Date(val);
                return (
                    <span className="text-slate-500 dark:text-slate-400 text-xs">
                        {date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                );
            },
        },
    ], []);

    const table = useReactTable({
        data: addresses,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualPagination: true,
        manualFiltering: true,
    });

    const SortIcon = ({ column }) => {
        const sorted = column.getIsSorted();
        if (sorted === 'asc') return <ArrowUpIcon className="w-3 h-3 text-blue-600" />;
        if (sorted === 'desc') return <ArrowDownIcon className="w-3 h-3 text-blue-600" />;
        return <ArrowsUpDownIcon className="w-3 h-3 text-slate-300 group-hover:text-slate-500 transition-colors" />;
    };

    return (
        <div className="p-6 space-y-5 max-w-[1600px] mx-auto">

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                        <BookOpenIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                            Adres Defteri
                        </h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                            B2B firma ve kişi rehberi
                        </p>
                    </div>
                </div>

                {/* Stats badge */}
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-sm font-semibold text-slate-600 dark:text-slate-300">
                    Toplam
                    <span className="text-blue-600 dark:text-blue-400 font-black text-base">{totalRecords}</span>
                    kayıt
                </div>
            </div>

            {/* Error banner */}
            {error && (
                <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400 font-medium">
                    {error}
                </div>
            )}

            {/* Table Card */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">

                {/* Search bar */}
                <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40">
                    <div className="relative max-w-sm">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Ad, şehir, ülke, e-posta ile ara..."
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white placeholder:text-slate-400 transition-all"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto relative">
                    {loading && (
                        <div className="absolute inset-0 z-10 bg-white/70 dark:bg-slate-900/70 backdrop-blur-[2px] flex items-center justify-center">
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-7 h-7 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
                                <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Yükleniyor</span>
                            </div>
                        </div>
                    )}

                    <table className="w-full text-sm text-left">
                        <thead>
                            {table.getHeaderGroups().map(hg => (
                                <tr key={hg.id} className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 text-[11px] font-bold uppercase tracking-wider">
                                    {hg.headers.map(header => (
                                        <th
                                            key={header.id}
                                            className="px-5 py-3.5 border-b border-slate-100 dark:border-slate-800 cursor-pointer select-none group hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors whitespace-nowrap"
                                            onClick={header.column.getToggleSortingHandler()}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIcon column={header.column} />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>

                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800/80">
                            {table.getRowModel().rows.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="px-5 py-16 text-center text-slate-400 dark:text-slate-600">
                                        <BookOpenIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                        <p className="font-semibold text-sm">Kayıt bulunamadı</p>
                                        {globalFilter && (
                                            <p className="text-xs mt-1">
                                                "<span className="text-blue-500">{globalFilter}</span>" araması için sonuç yok.
                                            </p>
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map(row => (
                                    <tr
                                        key={row.id}
                                        className="hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors"
                                    >
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id} className="px-5 py-3.5 dark:text-slate-300">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="px-5 py-3.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 flex items-center justify-between gap-4">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                        Sayfa{' '}
                        <span className="text-slate-900 dark:text-white font-bold">{currentPage}</span>
                        {' '}/ {totalPages}
                        <span className="ml-2 text-slate-400">({totalRecords} toplam kayıt)</span>
                    </span>

                    <div className="flex items-center gap-2">
                        <button
                            disabled={currentPage <= 1 || loading}
                            onClick={() => setCurrentPage(p => p - 1)}
                            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                        <button
                            disabled={currentPage >= totalPages || loading}
                            onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddressBookPage;
